import React, { useState } from "react";
import Layout from "../../layout/Layout";
import {
  Button,
  Card,
  Select,
  Tabs,
  Alert,
  Badge,
  Row,
  Col,
  Space,
  Typography,
  Statistic,
  List,
  Tag,
  // App
} from "antd";
import {
  PlayCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  MonitorOutlined,
  PrinterOutlined,
  CoffeeOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SettingOutlined,
  BranchesOutlined
} from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const RoutingSimulation = () => {
  const [selectedMode, setSelectedMode] = useState("menu-item");
  const [selectedMenuItem, setSelectedMenuItem] = useState("");
  const [selectedOrder, setSelectedOrder] = useState("");
  const [simulationResult, setSimulationResult] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  // const { message } = App.useApp();

  // Mock data
  const mockMenuItems = [
    {
      id: "item-001",
      name: "Grilled Chicken Breast",
      category: "Entrees",
      preparationTime: 12,
      allergens: ["dairy"],
      assignedStation: "Grill Station"
    },
    {
      id: "item-002",
      name: "Caesar Salad",
      category: "Salads",
      preparationTime: 5,
      allergens: ["dairy", "eggs"],
      assignedStation: "Cold Station"
    },
    {
      id: "item-003",
      name: "French Fries",
      category: "Sides",
      preparationTime: 8,
      assignedStation: "Grill Station"
    },
    {
      id: "item-004",
      name: "Latte",
      category: "Beverages",
      preparationTime: 3,
      assignedStation: "Beverage Station"
    },
    {
      id: "item-005",
      name: "Fish & Chips",
      category: "Entrees",
      preparationTime: 15,
      allergens: ["gluten"],
      assignedStation: "Grill Station"
    },
    {
      id: "item-006",
      name: "Soup of the Day",
      category: "Appetizers",
      preparationTime: 2
      // No assigned station - will cause error
    }
  ];

  const mockCompositeOrders = [
    {
      id: "order-001",
      orderType: "dine-in",
      totalItems: 4,
      items: [
        { menuItem: mockMenuItems[0], quantity: 2, seat: 1 },
        { menuItem: mockMenuItems[1], quantity: 1, seat: 1 },
        { menuItem: mockMenuItems[3], quantity: 1, seat: 2 }
      ]
    },
    {
      id: "order-002",
      orderType: "takeout",
      totalItems: 3,
      items: [
        { menuItem: mockMenuItems[4], quantity: 1 },
        { menuItem: mockMenuItems[2], quantity: 2 }
      ]
    },
    {
      id: "order-003",
      orderType: "dine-in",
      totalItems: 5,
      items: [
        { menuItem: mockMenuItems[0], quantity: 1, seat: 1 },
        { menuItem: mockMenuItems[5], quantity: 1, seat: 1 }, // This will cause error
        { menuItem: mockMenuItems[1], quantity: 2, seat: 2 },
        { menuItem: mockMenuItems[3], quantity: 1, seat: 2 }
      ]
    }
  ];

  const mockStations = [
    {
      id: "station-001",
      name: "Grill Station",
      outputMethod: "both",
      assignedKdsDevice: "KDS-001",
      assignedPrinter: "PRINTER-001",
      displayColor: "#EF4444"
    },
    {
      id: "station-002",
      name: "Cold Station",
      outputMethod: "kds",
      assignedKdsDevice: "KDS-002",
      displayColor: "#3B82F6"
    },
    {
      id: "station-003",
      name: "Beverage Station",
      outputMethod: "printer",
      assignedPrinter: "PRINTER-002",
      displayColor: "#F59E0B"
    }
  ];

  const simulateRouting = () => {
    setIsSimulating(true);
    
    // Simulate API delay
    setTimeout(() => {
      if (selectedMode === "menu-item" && selectedMenuItem) {
        const menuItem = mockMenuItems.find(item => item.id === selectedMenuItem);
        if (menuItem) {
          const result = simulateMenuItemRouting(menuItem);
          setSimulationResult(result);
        }
      } else if (selectedMode === "composite-order" && selectedOrder) {
        const order = mockCompositeOrders.find(order => order.id === selectedOrder);
        if (order) {
          const result = simulateCompositeOrderRouting(order);
          setSimulationResult(result);
        }
      }
      setIsSimulating(false);
      // message.success('Simulation completed successfully');
    }, 1000);
  };

  const simulateMenuItemRouting = (menuItem) => {
    const station = mockStations.find(s => s.name === menuItem.assignedStation);
    
    const route = {
      menuItemId: menuItem.id,
      assignedStation: menuItem.assignedStation,
      hasError: !station,
      errorMessage: !station ? "No valid station assigned for this menu item" : undefined
    };

    if (station) {
      route.outputMethod = station.outputMethod;
      route.targetDevice = station.assignedKdsDevice;
      route.targetPrinter = station.assignedPrinter;
      route.simulatedDisplay = generateSimulatedDisplay(menuItem, station);
    }

    return {
      orderId: `sim-${Date.now()}`,
      routes: [route],
      hasErrors: route.hasError,
      summary: {
        totalStations: station ? 1 : 0,
        kdsOutputs: station && (station.outputMethod === "kds" || station.outputMethod === "both") ? 1 : 0,
        printerOutputs: station && (station.outputMethod === "printer" || station.outputMethod === "both") ? 1 : 0,
        errors: route.hasError ? 1 : 0
      }
    };
  };

  const simulateCompositeOrderRouting = (order) => {
    const routes = [];
    
    order.items.forEach(orderItem => {
      const station = mockStations.find(s => s.name === orderItem.menuItem.assignedStation);
      
      const route = {
        menuItemId: orderItem.menuItem.id,
        assignedStation: orderItem.menuItem.assignedStation,
        hasError: !station,
        errorMessage: !station ? `No valid station assigned for ${orderItem.menuItem.name}` : undefined
      };

      if (station) {
        route.outputMethod = station.outputMethod;
        route.targetDevice = station.assignedKdsDevice;
        route.targetPrinter = station.assignedPrinter;
        route.simulatedDisplay = generateSimulatedDisplay(orderItem.menuItem, station, orderItem.seat);
      }

      routes.push(route);
    });

    const hasErrors = routes.some(r => r.hasError);
    const uniqueStations = new Set(routes.filter(r => !r.hasError).map(r => r.assignedStation)).size;
    const kdsOutputs = routes.filter(r => !r.hasError && (r.outputMethod === "kds" || r.outputMethod === "both")).length;
    const printerOutputs = routes.filter(r => !r.hasError && (r.outputMethod === "printer" || r.outputMethod === "both")).length;

    return {
      orderId: order.id,
      routes,
      hasErrors,
      summary: {
        totalStations: uniqueStations,
        kdsOutputs,
        printerOutputs,
        errors: routes.filter(r => r.hasError).length
      }
    };
  };

  const generateSimulatedDisplay = (menuItem, station, seat) => {
    const seatInfo = seat ? ` (Seat ${seat})` : "";
    return `[${station.name}] ${menuItem.name}${seatInfo} - ${menuItem.preparationTime}min`;
  };

  const resetSimulation = () => {
    setSimulationResult(null);
    setSelectedMenuItem("");
    setSelectedOrder("");
    // message.info('Simulation reset');
  };

  const getOutputMethodIcon = (method) => {
    switch (method) {
      case "kds":
        return <MonitorOutlined style={{ color: "#1890ff" }} />;
      case "printer":
        return <PrinterOutlined style={{ color: "#fa8c16" }} />;
      case "both":
        return (
          <Space size={4}>
            <MonitorOutlined style={{ color: "#1890ff" }} />
            <PrinterOutlined style={{ color: "#fa8c16" }} />
          </Space>
        );
      default:
        return <WarningOutlined style={{ color: "#ff4d4f" }} />;
    }
  };

  const getCategoryIcon = (category) => {
    return category.toLowerCase() === "beverages" ? <CoffeeOutlined /> : <SettingOutlined />;
  };

  return (
    <Layout>
      <div style={{ padding: "0 24px" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={2}>Routing Preview & Simulation</Title>
            <Paragraph>Test and preview routing logic for menu items and order types</Paragraph>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={resetSimulation}>
              Reset
            </Button>
            <Button 
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={isSimulating}
              disabled={!selectedMenuItem && !selectedOrder}
              onClick={simulateRouting}
            >
              {isSimulating ? "Simulating..." : "Run Simulation"}
            </Button>
          </Space>
        </div>

        <Row gutter={24}>
          {/* Simulation Setup */}
          <Col xs={24} lg={8}>
            <Card 
              title={
                <Space>
                  <BranchesOutlined />
                  Simulation Setup
                </Space>
              }
              style={{ marginBottom: "16px" }}
            >
              <div style={{ marginBottom: "16px" }}>
                <Text strong>Simulation Mode</Text>
                <Tabs
                  activeKey={selectedMode}
                  onChange={setSelectedMode}
                  style={{ marginTop: "8px" }}
                  items={[
                    {
                      key: "menu-item",
                      label: "Menu Item",
                      children: (
                        <div>
                          <div style={{ marginBottom: "16px" }}>
                            <Text strong>Select Menu Item</Text>
                            <Select
                              style={{ width: "100%", marginTop: "8px" }}
                              placeholder="Choose a menu item"
                              value={selectedMenuItem}
                              onChange={setSelectedMenuItem}
                              showSearch
                              optionFilterProp="children"
                            >
                              {mockMenuItems.map((item) => (
                                <Option key={item.id} value={item.id}>
                                  <Space>
                                    {getCategoryIcon(item.category)}
                                    {item.name}
                                    <Tag color="blue">{item.category}</Tag>
                                  </Space>
                                </Option>
                              ))}
                            </Select>
                          </div>

                          {selectedMenuItem && (
                            <Card size="small" style={{ backgroundColor: "#f9f9f9" }}>
                              {(() => {
                                const item = mockMenuItems.find(i => i.id === selectedMenuItem);
                                return item ? (
                                  <div>
                                    <Row justify="space-between" align="middle" style={{ marginBottom: "8px" }}>
                                      <Text strong>{item.name}</Text>
                                      <Tag color="blue">{item.category}</Tag>
                                    </Row>
                                    <Row gutter={16} style={{ marginBottom: "8px" }}>
                                      <Col>
                                        <Space size={4}>
                                          <ClockCircleOutlined />
                                          <Text type="secondary">{item.preparationTime}min</Text>
                                        </Space>
                                      </Col>
                                      {item.assignedStation && (
                                        <Col>
                                          <Space size={4}>
                                            <MonitorOutlined />
                                            <Text type="secondary">{item.assignedStation}</Text>
                                          </Space>
                                        </Col>
                                      )}
                                    </Row>
                                    {item.allergens && item.allergens.length > 0 && (
                                      <div>
                                        {item.allergens.map((allergen) => (
                                          <Tag key={allergen} color="orange" size="small">
                                            {allergen}
                                          </Tag>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : null;
                              })()}
                            </Card>
                          )}
                        </div>
                      )
                    },
                    {
                      key: "composite-order",
                      label: "Full Order",
                      children: (
                        <div>
                          <div style={{ marginBottom: "16px" }}>
                            <Text strong>Select Order</Text>
                            <Select
                              style={{ width: "100%", marginTop: "8px" }}
                              placeholder="Choose an order"
                              value={selectedOrder}
                              onChange={setSelectedOrder}
                            >
                              {mockCompositeOrders.map((order) => (
                                <Option key={order.id} value={order.id}>
                                  <Space>
                                    <UserOutlined />
                                    {order.id}
                                    <Tag color="green">{order.orderType}</Tag>
                                    <Text type="secondary">({order.totalItems} items)</Text>
                                  </Space>
                                </Option>
                              ))}
                            </Select>
                          </div>

                          {selectedOrder && (
                            <Card size="small" style={{ backgroundColor: "#f9f9f9" }}>
                              {(() => {
                                const order = mockCompositeOrders.find(o => o.id === selectedOrder);
                                return order ? (
                                  <div>
                                    <Row justify="space-between" align="middle" style={{ marginBottom: "8px" }}>
                                      <Text strong>{order.id}</Text>
                                      <Tag color="green">{order.orderType}</Tag>
                                    </Row>
                                    <div>
                                      {order.items.map((item, index) => (
                                        <Row key={index} justify="space-between" style={{ marginBottom: "4px" }}>
                                          <Text>{item.menuItem.name} x{item.quantity}</Text>
                                          {item.seat && (
                                            <Tag color="purple" size="small">
                                              Seat {item.seat}
                                            </Tag>
                                          )}
                                        </Row>
                                      ))}
                                    </div>
                                  </div>
                                ) : null;
                              })()}
                            </Card>
                          )}
                        </div>
                      )
                    }
                  ]}
                />
              </div>
            </Card>
          </Col>

          {/* Simulation Results */}
          <Col xs={24} lg={16}>
            {!simulationResult ? (
              <Card style={{ textAlign: "center", minHeight: "400px" }}>
                <div style={{ padding: "60px 20px" }}>
                  <BranchesOutlined style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: "16px" }} />
                  <Title level={4}>Ready to Simulate</Title>
                  <Paragraph type="secondary">
                    Select a menu item or composite order and click "Run Simulation" to preview routing logic
                  </Paragraph>
                </div>
              </Card>
            ) : (
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Summary */}
                <Card 
                  title={
                    <Space>
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      Simulation Summary
                    </Space>
                  }
                >
                  <Row gutter={16}>
                    <Col xs={12} md={6}>
                      <Statistic 
                        title="Stations" 
                        value={simulationResult.summary.totalStations}
                        valueStyle={{ color: "#1890ff" }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic 
                        title="KDS Outputs" 
                        value={simulationResult.summary.kdsOutputs}
                        valueStyle={{ color: "#52c41a" }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic 
                        title="Printer Outputs" 
                        value={simulationResult.summary.printerOutputs}
                        valueStyle={{ color: "#fa8c16" }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic 
                        title="Errors" 
                        value={simulationResult.summary.errors}
                        valueStyle={{ color: simulationResult.summary.errors > 0 ? "#ff4d4f" : "#52c41a" }}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Routing Results */}
                <Card title="Routing Results">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    {simulationResult.routes.map((route, index) => {
                      const menuItem = mockMenuItems.find(item => item.id === route.menuItemId);
                      const station = mockStations.find(s => s.name === route.assignedStation);
                      
                      return (
                        <Card
                          key={index}
                          size="small"
                          style={{
                            border: route.hasError ? "1px solid #ff4d4f" : "1px solid #52c41a",
                            backgroundColor: route.hasError ? "#fff2f0" : "#f6ffed"
                          }}
                        >
                          <Row justify="space-between" align="top">
                            <Col flex="1">
                              <div style={{ marginBottom: "8px" }}>
                                <Space>
                                  {getCategoryIcon(menuItem?.category || "")}
                                  <Text strong>{menuItem?.name}</Text>
                                  <Tag color="blue">{menuItem?.category}</Tag>
                                </Space>
                              </div>
                              
                              {route.hasError ? (
                                <Alert
                                  message="Routing Error"
                                  description={route.errorMessage}
                                  type="error"
                                  showIcon
                                  style={{ marginTop: "8px" }}
                                />
                              ) : (
                                <div>
                                  <Row gutter={16} style={{ marginBottom: "12px" }}>
                                    <Col xs={24} md={8}>
                                      <Text type="secondary">Station:</Text>
                                      <div style={{ marginTop: "4px" }}>
                                        <Space>
                                          <div
                                            style={{
                                              width: "12px",
                                              height: "12px",
                                              borderRadius: "50%",
                                              backgroundColor: station?.displayColor
                                            }}
                                          />
                                          <Text strong>{route.assignedStation}</Text>
                                        </Space>
                                      </div>
                                    </Col>
                                    <Col xs={24} md={8}>
                                      <Text type="secondary">Output Method:</Text>
                                      <div style={{ marginTop: "4px" }}>
                                        <Space>
                                          {getOutputMethodIcon(route.outputMethod)}
                                          <Text strong style={{ textTransform: "capitalize" }}>
                                            {route.outputMethod}
                                          </Text>
                                        </Space>
                                      </div>
                                    </Col>
                                    <Col xs={24} md={8}>
                                      <Text type="secondary">Devices:</Text>
                                      <div style={{ marginTop: "4px" }}>
                                        <Space direction="vertical" size={2}>
                                          {route.targetDevice && (
                                            <Space size={4}>
                                              <MonitorOutlined style={{ color: "#1890ff" }} />
                                              <Text style={{ fontSize: "12px" }}>{route.targetDevice}</Text>
                                            </Space>
                                          )}
                                          {route.targetPrinter && (
                                            <Space size={4}>
                                              <PrinterOutlined style={{ color: "#fa8c16" }} />
                                              <Text style={{ fontSize: "12px" }}>{route.targetPrinter}</Text>
                                            </Space>
                                          )}
                                        </Space>
                                      </div>
                                    </Col>
                                  </Row>
                                  
                                  {route.simulatedDisplay && (
                                    <div style={{
                                      marginTop: "12px",
                                      padding: "8px",
                                      backgroundColor: "#f5f5f5",
                                      borderRadius: "4px",
                                      fontFamily: "monospace",
                                      fontSize: "12px"
                                    }}>
                                      <Text type="secondary">Simulated Display:</Text>
                                      <div style={{ marginTop: "4px" }}>{route.simulatedDisplay}</div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Col>
                            
                            <Col style={{ marginLeft: "16px" }}>
                              {route.hasError ? (
                                <WarningOutlined style={{ fontSize: "24px", color: "#ff4d4f" }} />
                              ) : (
                                <CheckCircleOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
                              )}
                            </Col>
                          </Row>
                        </Card>
                      );
                    })}
                  </Space>
                </Card>
              </Space>
            )}
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default RoutingSimulation;
